import React from 'react';
import './PipelineHealthPanels.css';

// Modern Pipeline Health dashboard. Replaces the legacy "engagement funnel"
// scene-posting + engager-promotion focus with the reply-ops pipeline view.
// Backend endpoint: /api/admin/pipeline-health.

const fmtPct = (v) => (v == null ? '–' : `${v.toFixed ? v.toFixed(1) : v}%`);
const fmtNum = (v) => (v == null ? '–' : Number(v).toLocaleString());

const stageColor = (pct, target = 80) => {
  if (pct == null) return '#666';
  if (pct >= target) return '#4caf50';
  if (pct >= target * 0.5) return '#ffb74d';
  return '#e57373';
};

const PipelineFunnel = ({ funnel, days }) => {
  if (!funnel) return null;
  const stages = [
    { key: 'scanned', label: 'Scanned', value: funnel.scanned, pct: 100 },
    { key: 'classified', label: 'Classified', value: funnel.classified, pct: funnel.classified_pct, gateTarget: 90, gateNote: 'IE-030 gate' },
    { key: 'routed', label: 'Routed', value: funnel.routed, pct: funnel.routed_pct },
    { key: 'screened', label: 'Screened', value: funnel.screened, pct: funnel.screened_pct, gateTarget: 80, gateNote: 'IE-029 gate' },
    { key: 'posted', label: 'Posted', value: funnel.posted, pct: funnel.posted_pct_of_routed, denomLabel: 'of routed' },
    { key: 'engaged', label: 'Engaged', value: funnel.engaged, pct: funnel.engaged_pct_of_posted, denomLabel: 'of posted' },
  ];
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Pipeline funnel ({days}d)</h4>
        <span className="ph-section-sub">
          {fmtNum(funnel.expired)} expired · {fmtNum(funnel.skipped)} skipped · {fmtNum(funnel.pending)} pending
          {funnel.expired_to_posted_ratio != null && (
            <> · expired:posted {funnel.expired_to_posted_ratio}:1</>
          )}
        </span>
      </div>
      <div className="ph-funnel-row">
        {stages.map((s, i) => (
          <React.Fragment key={s.key}>
            <div className="ph-funnel-stage">
              <div className="ph-funnel-num">{fmtNum(s.value)}</div>
              <div className="ph-funnel-label">{s.label}</div>
              <div
                className="ph-funnel-pct"
                style={{ color: stageColor(s.pct, s.gateTarget || 80) }}
                title={s.gateNote ? `Target ${s.gateTarget}% (${s.gateNote})` : ''}
              >
                {fmtPct(s.pct)}{s.denomLabel ? <span className="ph-funnel-denom"> {s.denomLabel}</span> : null}
              </div>
            </div>
            {i < stages.length - 1 && <span className="ph-funnel-arrow">→</span>}
          </React.Fragment>
        ))}
      </div>
      {funnel.routed_null_best > 0 && (
        <div className="ph-section-note">
          {funnel.routed_null_best_pct}% of routed had no character above floor
          ({fmtNum(funnel.routed_null_best)} opps; filtered by composer per IE-027f).
        </div>
      )}
    </div>
  );
};

const CharacterScoreboard = ({ characters, days }) => {
  if (!characters || characters.length === 0) {
    return (
      <div className="ph-section">
        <div className="ph-section-header"><h4>Per-character scoreboard ({days}d)</h4></div>
        <div className="ph-empty">No posts in this window.</div>
      </div>
    );
  }
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Per-character scoreboard ({days}d)</h4>
        <span className="ph-section-sub">hit rate = posts that received any like or repost</span>
      </div>
      <table className="ph-table">
        <thead>
          <tr>
            <th>Character</th>
            <th className="num">Posted</th>
            <th className="num">Engaged</th>
            <th className="num">Hit rate</th>
            <th className="num">Mean</th>
            <th className="num">Median</th>
            <th className="num">Likes</th>
            <th className="num">Reposts</th>
            <th className="num">Routing (rubric / rotation / other)</th>
          </tr>
        </thead>
        <tbody>
          {characters.map((c) => (
            <tr key={c.name}>
              <td><strong>{c.name}</strong></td>
              <td className="num">{fmtNum(c.posted)}</td>
              <td className="num">{fmtNum(c.engaged)}</td>
              <td className="num" style={{ color: stageColor(c.hit_rate_pct, 50) }}>
                {fmtPct(c.hit_rate_pct)}
              </td>
              <td className="num">{c.mean_engagement.toFixed(2)}</td>
              <td className="num">{c.median_engagement.toFixed(2)}</td>
              <td className="num">{fmtNum(c.total_likes)}</td>
              <td className="num">{fmtNum(c.total_reposts)}</td>
              <td className="num ph-routing-split">
                <span title="rubric-routed">{c.rubric_posted}</span>
                {' / '}
                <span title="rotation-routed">{c.rotation_posted}</span>
                {(c.hybrid_posted > 0 || c.unmarked_posted > 0) && (
                  <> {' / '}<span title="hybrid + untagged">{c.hybrid_posted + c.unmarked_posted}</span></>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const RoutingABPanel = ({ routingAb, days }) => {
  const overall = routingAb?.overall || {};
  const byChar = routingAb?.by_character || {};
  const arms = ['rubric', 'rotation'];
  const haveData = arms.some((a) => overall[a]);
  if (!haveData) {
    return (
      <div className="ph-section">
        <div className="ph-section-header"><h4>Routing A/B — rubric vs rotation ({days}d)</h4></div>
        <div className="ph-empty">No routing-tagged posts in this window.</div>
      </div>
    );
  }
  const rubric = overall.rubric || {};
  const rotation = overall.rotation || {};
  const lift = (rubric.hit_rate_pct != null && rotation.hit_rate_pct != null)
    ? (rubric.hit_rate_pct - rotation.hit_rate_pct).toFixed(1)
    : null;
  const liftClass = lift == null ? '' : (parseFloat(lift) >= 0 ? 'ph-lift-positive' : 'ph-lift-negative');

  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Routing A/B — rubric vs rotation ({days}d)</h4>
        <span className="ph-section-sub">IE-028 readout. n &lt; 50/arm = preliminary.</span>
      </div>
      <div className="ph-ab-summary">
        <div className="ph-ab-arm ph-ab-rubric">
          <div className="ph-ab-arm-name">Rubric</div>
          <div className="ph-ab-arm-stats">
            <div><strong>{fmtNum(rubric.posted)}</strong> posted</div>
            <div><strong>{fmtPct(rubric.hit_rate_pct)}</strong> hit rate</div>
            <div>{rubric.mean_engagement?.toFixed(2)} mean · {rubric.median_engagement?.toFixed(2)} median</div>
          </div>
        </div>
        <div className={`ph-ab-lift ${liftClass}`}>
          {lift != null ? `${lift > 0 ? '+' : ''}${lift} pp` : '–'}
          <div className="ph-ab-lift-label">rubric − rotation hit rate</div>
        </div>
        <div className="ph-ab-arm ph-ab-rotation">
          <div className="ph-ab-arm-name">Rotation</div>
          <div className="ph-ab-arm-stats">
            <div><strong>{fmtNum(rotation.posted)}</strong> posted</div>
            <div><strong>{fmtPct(rotation.hit_rate_pct)}</strong> hit rate</div>
            <div>{rotation.mean_engagement?.toFixed(2)} mean · {rotation.median_engagement?.toFixed(2)} median</div>
          </div>
        </div>
      </div>

      {Object.keys(byChar).length > 0 && (
        <table className="ph-table ph-table-compact">
          <thead>
            <tr>
              <th>Character</th>
              <th className="num">Rubric n</th>
              <th className="num">Rubric hit</th>
              <th className="num">Rubric mean</th>
              <th className="num">Rotation n</th>
              <th className="num">Rotation hit</th>
              <th className="num">Rotation mean</th>
              <th className="num">Lift (pp)</th>
            </tr>
          </thead>
          <tbody>
            {Object.keys(byChar).sort().map((name) => {
              const c = byChar[name];
              const r = c.rubric || {};
              const o = c.rotation || {};
              const charLift = (r.hit_rate_pct != null && o.hit_rate_pct != null)
                ? (r.hit_rate_pct - o.hit_rate_pct).toFixed(1)
                : null;
              return (
                <tr key={name}>
                  <td><strong>{name}</strong></td>
                  <td className="num">{fmtNum(r.posted ?? 0)}</td>
                  <td className="num">{r.hit_rate_pct != null ? fmtPct(r.hit_rate_pct) : '–'}</td>
                  <td className="num">{r.mean_engagement != null ? r.mean_engagement.toFixed(2) : '–'}</td>
                  <td className="num">{fmtNum(o.posted ?? 0)}</td>
                  <td className="num">{o.hit_rate_pct != null ? fmtPct(o.hit_rate_pct) : '–'}</td>
                  <td className="num">{o.mean_engagement != null ? o.mean_engagement.toFixed(2) : '–'}</td>
                  <td className={`num ${charLift != null ? (parseFloat(charLift) >= 0 ? 'ph-lift-positive' : 'ph-lift-negative') : ''}`}>
                    {charLift != null ? `${charLift > 0 ? '+' : ''}${charLift}` : '–'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

const RiskDistribution = ({ riskDistribution, days }) => {
  if (!riskDistribution || riskDistribution.length === 0) return null;
  const total = riskDistribution.reduce((s, r) => s + (r.n || 0), 0);
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Risk distribution ({days}d)</h4>
        <span className="ph-section-sub">IE-029 prep — caution overrides + posted/engaged by risk class.</span>
      </div>
      <table className="ph-table ph-table-compact">
        <thead>
          <tr>
            <th>Risk</th>
            <th className="num">N</th>
            <th className="num">% of pool</th>
            <th className="num">Posted</th>
            <th className="num">Engaged</th>
            <th className="num">Skipped</th>
            <th className="num">Override hit rate</th>
          </tr>
        </thead>
        <tbody>
          {riskDistribution.map((r) => {
            const overrideRate = r.posted > 0 ? (100 * r.engaged / r.posted) : null;
            return (
              <tr key={r.risk}>
                <td><strong>{r.risk}</strong></td>
                <td className="num">{fmtNum(r.n)}</td>
                <td className="num">{total ? fmtPct(100 * r.n / total) : '–'}</td>
                <td className="num">{fmtNum(r.posted)}</td>
                <td className="num">{fmtNum(r.engaged)}</td>
                <td className="num">{fmtNum(r.skipped)}</td>
                <td className="num">{overrideRate != null ? fmtPct(overrideRate) : '–'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const SlotFill = ({ slotFill, days }) => {
  if (!slotFill || slotFill.length === 0) return null;
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Slot fill ({days}d) — IE-020</h4>
        <span className="ph-section-sub">Per-slot engagement. Compare slot types for compositional balance.</span>
      </div>
      <table className="ph-table ph-table-compact">
        <thead>
          <tr>
            <th>Slot</th>
            <th className="num">Posted</th>
            <th className="num">Engaged</th>
            <th className="num">Hit rate</th>
            <th className="num">Mean engagement</th>
          </tr>
        </thead>
        <tbody>
          {slotFill.map((s) => (
            <tr key={s.slot_type}>
              <td><strong>{s.slot_type}</strong></td>
              <td className="num">{fmtNum(s.posted)}</td>
              <td className="num">{fmtNum(s.engaged)}</td>
              <td className="num" style={{ color: stageColor(s.hit_rate_pct, 50) }}>
                {fmtPct(s.hit_rate_pct)}
              </td>
              <td className="num">{s.mean_engagement.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const Gates = ({ gates }) => {
  if (!gates) return null;
  const items = [
    { key: 'cue_screen_coverage', g: gates.cue_screen_coverage },
    { key: 'per_author_cap', g: gates.per_author_cap },
    { key: 'classifier_coverage_pending', g: gates.classifier_coverage_pending },
    { key: 'scout_alive', g: gates.scout_alive },
    { key: 'approval_rate_alive', g: gates.approval_rate_alive },
  ].filter((x) => x.g);
  if (!items.length) return null;
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Readiness gates</h4>
        <span className="ph-section-sub">When all green, IE-029 / IE-030 are unblocked.</span>
      </div>
      <div className="ph-gates">
        {items.map(({ key, g }) => {
          const passing = g.passing;
          const value = g.value_pct != null
            ? fmtPct(g.value_pct)
            : (g.value != null ? fmtNum(g.value) : '–');
          const target = g.target_pct != null
            ? `target ≥ ${g.target_pct}%`
            : (g.target != null ? `target ≤ ${g.target}` : '');
          return (
            <div key={key} className={`ph-gate ${passing ? 'ph-gate-pass' : 'ph-gate-fail'}`}>
              <div className="ph-gate-head">
                <span className="ph-gate-status">{passing ? '✓' : '◯'}</span>
                <span className="ph-gate-experiment">{g.experiment}</span>
              </div>
              <div className="ph-gate-label">{g.label}</div>
              <div className="ph-gate-value">{value}</div>
              <div className="ph-gate-target">{target}</div>
              {g.numerator != null && g.denominator != null && (
                <div className="ph-gate-frac">{fmtNum(g.numerator)} / {fmtNum(g.denominator)}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TopReplies = ({ replies, days }) => {
  if (!replies || replies.length === 0) return null;
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Top performing reply opportunities ({days}d)</h4>
        <span className="ph-section-sub">From reply_opportunities. Score = likes×10 + reposts×15 + replies×12.</span>
      </div>
      <ul className="ph-top-replies">
        {replies.map((r) => (
          <li key={r.id} className="ph-top-reply">
            <div className="ph-top-reply-meta">
              <strong className="ph-top-reply-character">{r.character || '?'}</strong>
              <span className="ph-top-reply-author">replied to @{r.author}</span>
              {r.routing_method && (
                <span className={`ph-top-reply-tag ph-tag-${r.routing_method}`}>
                  {r.routing_method}
                </span>
              )}
              {r.slot_type && (
                <span className="ph-top-reply-tag ph-tag-slot">{r.slot_type}</span>
              )}
              <span className="ph-top-reply-engagement">
                💙 {r.likes} · 🔁 {r.reposts}{r.replies > 0 ? ` · 💬 ${r.replies}` : ''}
              </span>
              <span className="ph-top-reply-score">score {r.score}</span>
            </div>
            <div className="ph-top-reply-text">
              <span className="ph-top-reply-quote">"{r.post_text}"</span>
              {r.reply_text && (
                <span className="ph-top-reply-arrow"> → "{r.reply_text}"</span>
              )}
            </div>
            {r.post_url && (
              <div className="ph-top-reply-link">
                <a href={r.post_url} target="_blank" rel="noopener noreferrer">view on Bluesky</a>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
};

// ─── Diversification panels (Apr 23 IE-032..IE-036 plan) ──────────────────

const ApprovalRateTrend = ({ trend }) => {
  if (!trend || !trend.length || trend.error) return null;
  const recent = trend.slice(-14);
  const max = 100;
  const latest = recent[recent.length - 1];
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Approval-rate trend (14d)</h4>
        <span className="ph-section-sub">
          Posted ÷ (posted + skipped) per day. Sustained drop = slate quality regressing.
          {latest && latest.approval_rate_pct != null && (
            <> · latest {latest.approval_rate_pct}%</>
          )}
        </span>
      </div>
      <div className="ph-spark-row">
        {recent.map((d) => {
          const v = d.approval_rate_pct;
          const h = v == null ? 0 : Math.max(2, Math.round((v / max) * 60));
          const color = v == null ? '#666' : v >= 60 ? '#4caf50' : v >= 40 ? '#ffb74d' : '#e57373';
          return (
            <div key={d.day} className="ph-spark-bar-wrap" title={`${d.day}: ${v ?? '–'}% (${d.posted}p/${d.skipped}s)`}>
              <div className="ph-spark-bar" style={{ height: `${h}px`, backgroundColor: color }} />
              <div className="ph-spark-label">{d.day.slice(5)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const PerCharacterLift = ({ lift, days }) => {
  if (!lift || !lift.length || lift.error) return null;
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Per-character routing lift ({days}d)</h4>
        <span className="ph-section-sub">
          Rubric hit-rate − rotation hit-rate. Negative = rubric losing for this character.
          Threshold for IE-027g audit: rubric n ≥ 10 AND lift ≤ −5pp.
        </span>
      </div>
      <table className="ph-table">
        <thead>
          <tr>
            <th>Character</th>
            <th className="ph-num">Rubric n</th>
            <th className="ph-num">Rubric hit</th>
            <th className="ph-num">Rotation n</th>
            <th className="ph-num">Rotation hit</th>
            <th className="ph-num">Lift (pp)</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {lift.map((r) => {
            const flag = r.rubric_n >= 10 && r.lift_pp != null && r.lift_pp <= -5;
            return (
              <tr key={r.character} className={flag ? 'ph-row-warn' : ''}>
                <td>{r.character}</td>
                <td className="ph-num">{fmtNum(r.rubric_n)}</td>
                <td className="ph-num">{fmtPct(r.rubric_hit_pct)}</td>
                <td className="ph-num">{fmtNum(r.rotation_n)}</td>
                <td className="ph-num">{fmtPct(r.rotation_hit_pct)}</td>
                <td className="ph-num">
                  {r.lift_pp == null ? '–' : (r.lift_pp > 0 ? `+${r.lift_pp}` : r.lift_pp)}
                </td>
                <td>{flag ? 'IE-027g audit' : ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ClassifierCoverageTrend = ({ trend }) => {
  if (!trend || !trend.length || trend.error) return null;
  const recent = trend.slice(-14);
  const latest = recent[recent.length - 1];
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Classifier coverage trend (14d)</h4>
        <span className="ph-section-sub">
          Daily coverage = classified ÷ surfaced. Sustained drop = classifier worker stalled.
          {latest && (<> · latest {fmtPct(latest.coverage_pct)}</>)}
        </span>
      </div>
      <div className="ph-spark-row">
        {recent.map((d) => {
          const h = Math.max(2, Math.round((d.coverage_pct / 100) * 60));
          const color = d.coverage_pct >= 90 ? '#4caf50' : d.coverage_pct >= 70 ? '#ffb74d' : '#e57373';
          return (
            <div key={d.day} className="ph-spark-bar-wrap" title={`${d.day}: ${d.coverage_pct}% (${d.classified}/${d.surfaced})`}>
              <div className="ph-spark-bar" style={{ height: `${h}px`, backgroundColor: color }} />
              <div className="ph-spark-label">{d.day.slice(5)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const YieldBySource = ({ rows, days }) => {
  if (!rows || !rows.length || rows.error) return null;
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Yield by source ({days}d)</h4>
        <span className="ph-section-sub">
          Per-source share of surfaced opps + posting hit rate. Watch for collapsed sources (e.g. scout 0).
        </span>
      </div>
      <table className="ph-table">
        <thead>
          <tr>
            <th>Source</th>
            <th className="ph-num">Surfaced</th>
            <th className="ph-num">Posted</th>
            <th className="ph-num">Engaged</th>
            <th className="ph-num">Hit rate</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const dead = r.surfaced > 0 && r.posted === 0;
            return (
              <tr key={r.source} className={dead ? 'ph-row-warn' : ''}>
                <td>{r.source}</td>
                <td className="ph-num">{fmtNum(r.surfaced)}</td>
                <td className="ph-num">{fmtNum(r.posted)}</td>
                <td className="ph-num">{fmtNum(r.engaged)}</td>
                <td className="ph-num">{fmtPct(r.hit_rate_pct)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const PoolComposition = ({ rows, queues }) => {
  if (!rows || !rows.length || rows.error) return null;
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Pool composition (active target_accounts)</h4>
        <span className="ph-section-sub">
          By source_type. Pool tab pending counts:
          {queues?.pending_demotions?.pending != null && (
            <> demotions {queues.pending_demotions.pending}</>
          )}
          {queues?.mining_candidates?.pending != null && (
            <> · mining {queues.mining_candidates.pending}</>
          )}
        </span>
      </div>
      <table className="ph-table">
        <thead>
          <tr>
            <th>Source type</th>
            <th className="ph-num">Active accounts</th>
            <th className="ph-num">Mean perf score</th>
            <th>Most recent post</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.source_type}>
              <td>{r.source_type}</td>
              <td className="ph-num">{fmtNum(r.active_accounts)}</td>
              <td className="ph-num">{r.mean_performance_score?.toFixed(3) ?? '–'}</td>
              <td>{r.most_recent_post ? r.most_recent_post.slice(0, 10) : '–'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ScoutDailyTrend = ({ daily, last24 }) => {
  if (!daily || daily.error) return null;
  return (
    <div className="ph-section">
      <div className="ph-section-header">
        <h4>Scout daily yield</h4>
        <span className="ph-section-sub">
          Stored opps per day from source='scout'. Last 24h: <strong>{last24 ?? 0}</strong>.
          0 yield for 3+ days → IE-032b admission fix.
        </span>
      </div>
      <div className="ph-spark-row">
        {(daily.length ? daily : [{ day: '—', stored: 0 }]).slice(-14).map((d, i) => {
          const max = Math.max(...daily.map((x) => x.stored), 1);
          const h = Math.max(2, Math.round((d.stored / max) * 60));
          const color = d.stored === 0 ? '#e57373' : '#4caf50';
          return (
            <div key={`${d.day}-${i}`} className="ph-spark-bar-wrap" title={`${d.day}: ${d.stored} stored`}>
              <div className="ph-spark-bar" style={{ height: `${h}px`, backgroundColor: color }} />
              <div className="ph-spark-label">{d.day.slice(5)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DiversificationPanels = ({ d, days }) => {
  if (!d) return null;
  return (
    <>
      <ApprovalRateTrend trend={d.approval_rate_trend} />
      <PerCharacterLift lift={d.per_character_lift} days={days} />
      <ClassifierCoverageTrend trend={d.classifier_coverage_trend} />
      <YieldBySource rows={d.yield_by_source} days={days} />
      <ScoutDailyTrend daily={d.scout_daily} last24={d.scout_last_24h_stored} />
      <PoolComposition
        rows={d.pool_composition}
        queues={{
          pending_demotions: d.pending_demotions,
          mining_candidates: d.mining_candidates,
        }}
      />
    </>
  );
};

const PipelineHealthPanels = ({ data }) => {
  if (!data) return null;
  const days = data.period_days || 7;
  return (
    <div className="ph-root">
      <Gates gates={data.gates} />
      <PipelineFunnel funnel={data.funnel} days={days} />
      <CharacterScoreboard characters={data.characters} days={days} />
      <RoutingABPanel routingAb={data.routing_ab} days={days} />
      <DiversificationPanels d={data.diversification} days={days} />
      <RiskDistribution riskDistribution={data.risk_distribution} days={days} />
      <SlotFill slotFill={data.slot_fill} days={days} />
      <TopReplies replies={data.top_replies} days={days} />
      {data.generated_at && (
        <div className="ph-footer">
          Generated {data.generated_at} · 60s cache
        </div>
      )}
    </div>
  );
};

export default PipelineHealthPanels;
